import React, { useState, useRef, useCallback, useEffect } from 'react'
import '../style/PartnnerDocuments.css'
import { host } from '../script/variables'

const BASE_URL = host

const authHeaders = () => {
  const token = localStorage.getItem('partnertoken')
  return { Authorization: `Bearer ${token}` }
}

const api = {
  // Upload a document
  uploadDocument: async ({ category, docType, restaurantId, file }) => {
    const form = new FormData()
    form.append('file', file)
    form.append('category', category)
    form.append('docType', docType)
    if (restaurantId) form.append('restaurantId', restaurantId)

    const res = await fetch(`${BASE_URL}/api/partnerdocs/documents/upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    })
    if (!res.ok) throw new Error(`Upload failed (${res.status})`)
    return res.json()
  },

  // Delete a document
  deleteDocument: async (docId, docKey) => {
    const res = await fetch(`${BASE_URL}/api/partnerdocs/documents/${docId}`, {
      method: 'DELETE',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ docKey }),
    })
    if (!res.ok) throw new Error(`Delete failed (${res.status})`)
    return res.json()
  },

  // Fetch documents — accepts a single restaurantId OR none (for owner)
  fetchDocuments: async (restaurantIds = []) => {
    const query = restaurantIds.length ? `?restaurantIds=${restaurantIds.join(',')}` : ''
    const res = await fetch(`${BASE_URL}/api/partnerdocs/documents${query}`, {
      headers: authHeaders(),
    })
    if (!res.ok) throw new Error(`Fetch failed (${res.status})`)
    return res.json()
  },

  // Submit for verification
  submitForVerification: async ({ category, restaurantId }) => {
    const res = await fetch(`${BASE_URL}/api/partnerdocs/documents/submit`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, restaurantId }),
    })
    if (!res.ok) throw new Error(`Submit failed (${res.status})`)
    return res.json()
  },

  /**
   * Get a fresh presigned S3 URL for a single file.
   * disposition: 'inline'     → view in browser tab
   *              'attachment' → force download
   */
  getSignedUrl: async (docId, docKey, disposition = 'inline') => {
    const res = await fetch(
      `${BASE_URL}/api/partnerdocs/documents/${docId}/signed-url?docKey=${encodeURIComponent(docKey)}&disposition=${disposition}`,
      { headers: authHeaders() }
    )
    if (!res.ok) throw new Error(`Failed to get signed URL (${res.status})`)
    return res.json()  // { url, disposition, originalName, mimeType, expiresIn }
  },
}


// ─────────────────────────────────────────────────────────────────────────────
// Document Config
// ─────────────────────────────────────────────────────────────────────────────
const OWNER_DOCS = [
  { key: 'aadhaar_front', label: 'Aadhaar Card — Front',             required: true,  accept: 'image/*,.pdf', hint: 'Front side of your Aadhaar card',                        section: 'kyc'     },
  { key: 'aadhaar_back',  label: 'Aadhaar Card — Back',              required: true,  accept: 'image/*,.pdf', hint: 'Back side of your Aadhaar card',                         section: 'kyc'     },
  { key: 'pan_card',      label: 'PAN Card',                         required: true,  accept: 'image/*,.pdf', hint: 'PAN card issued by the Income Tax Department',           section: 'kyc'     },
  { key: 'profile_photo', label: 'Profile Photo',                    required: true,  accept: 'image/*',      hint: 'Clear front-facing photo, plain background preferred',  section: 'profile' },
  { key: 'bank_passbook', label: 'Bank Passbook / Cancelled Cheque', required: true,  accept: 'image/*,.pdf', hint: 'First page of passbook or a cancelled cheque leaf',     section: 'profile' },
]

const RESTAURANT_DOCS = [
  { key: 'fssai',         label: 'FSSAI License',                    required: true,  accept: 'image/*,.pdf', hint: 'Food Safety and Standards Authority of India license',   section: 'safety'  },
  { key: 'gst',           label: 'GST Registration Certificate',     required: true,  accept: 'image/*,.pdf', hint: 'GSTIN certificate downloaded from the GST portal',       section: 'safety'  },
  { key: 'shop_act',      label: 'Shop & Establishment Act License', required: true,  accept: 'image/*,.pdf', hint: 'Issued by local municipal corporation',                  section: 'safety'  },
  { key: 'fire_noc',      label: 'Fire NOC',                        required: false, accept: 'image/*,.pdf', hint: 'No-objection certificate from Fire Department (if req.)', section: 'permits' },
  { key: 'eating_house',  label: 'Eating House License',            required: false, accept: 'image/*,.pdf', hint: 'Required in select states — Delhi, Maharashtra, etc.',   section: 'permits' },
  { key: 'trade_license', label: 'Trade / Municipal License',       required: false, accept: 'image/*,.pdf', hint: 'Issued by local municipal body',                         section: 'permits' },
]

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const formatBytes = (b) =>
  b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`

const isImageFile = (name = '') => /\.(jpe?g|png|webp|gif|bmp|heic)$/i.test(name)

// ─────────────────────────────────────────────────────────────────────────────
// StatusPill
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  uploaded: { label: 'Uploaded',       dot: '🟢' },
  verified: { label: 'Verified',       dot: '✅' },
  pending:  { label: 'Pending Review', dot: '🟡' },
  rejected: { label: 'Rejected',       dot: '🔴' },
  missing:  { label: 'Not Uploaded',   dot: '⬜' },
}

const StatusPill = ({ status = 'missing' }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.missing
  return (
    <span className={`pd-status-pill pd-status-${status}`}>
      {cfg.dot} {cfg.label}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// UploadZone
// ─────────────────────────────────────────────────────────────────────────────
// FileCard — shown when a file has been uploaded
// Calls the signed-url endpoint on demand so URLs are always fresh
// ─────────────────────────────────────────────────────────────────────────────
const FileCard = ({ uploadedFile, onDelete }) => {
  const [loadingView, setLoadingView]     = useState(false)
  const [loadingDl, setLoadingDl]         = useState(false)
  const [urlError, setUrlError]           = useState(null)

  const showThumb = isImageFile(uploadedFile.name || '')

  // Fetch a fresh signed URL then open/download it
  const handleAction = async (disposition) => {
    const isDl = disposition === 'attachment'
    isDl ? setLoadingDl(true) : setLoadingView(true)
    setUrlError(null)

    try {
      const { url, originalName } = await api.getSignedUrl(
        uploadedFile.id,
        uploadedFile.docKey,
        disposition
      )

      if (isDl) {
        // Trigger download without leaving the page
        const a = document.createElement('a')
        a.href     = url
        a.download = originalName || uploadedFile.name || 'document'
        a.rel      = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        // Open preview in new tab
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    } catch (err) {
      setUrlError('Could not load file. Try again.')
    } finally {
      isDl ? setLoadingDl(false) : setLoadingView(false)
    }
  }

  return (
    <div className="pd-file-card">
      {showThumb ? (
        <img
          src={uploadedFile.url}
          alt={uploadedFile.name}
          className="pd-file-thumb"
          onError={(e) => { e.target.style.display = 'none' }}
        />
      ) : (
        <div className="pd-file-icon">📄</div>
      )}

      <div className="pd-file-meta">
        <div className="pd-file-name" title={uploadedFile.name}>{uploadedFile.name}</div>
        <div className="pd-file-size">
          {uploadedFile.size ? `${formatBytes(uploadedFile.size)} · ` : ''}
          <StatusPill status={uploadedFile.status || 'uploaded'} />
        </div>
        {uploadedFile.uploadedAt && (
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
            {new Date(uploadedFile.uploadedAt).toLocaleString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </div>
        )}
        {urlError && (
          <div style={{ fontSize: 10, color: 'var(--rust)', marginTop: 3 }}>{urlError}</div>
        )}
      </div>

      <div className="pd-file-actions">
        {/* View in browser tab */}
        <button
          className="pd-file-view-btn"
          onClick={() => handleAction('inline')}
          disabled={loadingView || loadingDl}
          title="View in browser"
        >
          {loadingView ? '…' : 'View ↗'}
        </button>

        {/* Download */}
        <button
          className="pd-file-view-btn pd-file-dl-btn"
          onClick={() => handleAction('attachment')}
          disabled={loadingView || loadingDl}
          title="Download file"
        >
          {loadingDl ? '…' : (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M6.5 1v8M3 6l3.5 3.5L10 6M1 12h11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>

        {/* Delete */}
        <button className="pd-file-delete-btn" onClick={onDelete} title="Remove document">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// UploadZone
// ─────────────────────────────────────────────────────────────────────────────
const UploadZone = ({ docConfig, uploadedFile, onUpload, onDelete, uploading }) => {
  const inputRef = useRef()
  const [dragOver, setDragOver] = useState(false)

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) onUpload(file)
  }, [onUpload])

  if (uploading) {
    return (
      <div className="pd-upload-zone uploading">
        <div className="pd-spinner">
          <div className="pd-spinner-ring" />
          Uploading document…
        </div>
      </div>
    )
  }

  if (uploadedFile) {
    return <FileCard uploadedFile={uploadedFile} onDelete={onDelete} />
  }

  return (
    <div
      className={`pd-upload-zone${dragOver ? ' drag-over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
    >
      <span className="pd-upload-zone-icon">☁️</span>
      <div className="pd-upload-zone-title">
        Drop file here or <span>browse</span>
      </div>
      <div className="pd-upload-zone-hint">{docConfig.hint}</div>
      <div className="pd-upload-zone-formats">JPG · PNG · PDF · max 10 MB</div>
      <input
        ref={inputRef}
        type="file"
        accept={docConfig.accept}
        style={{ display: 'none' }}
        onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// DocRow
// ─────────────────────────────────────────────────────────────────────────────
const DocRow = ({ docConfig, uploadedFile, onUpload, onDelete, uploading }) => (
  <div className="pd-doc-row">
    <div className="pd-doc-row-header">
      <span className="pd-doc-row-label">{docConfig.label}</span>
      <span className={`pd-badge ${docConfig.required ? 'pd-badge-required' : 'pd-badge-optional'}`}>
        {docConfig.required ? 'Required' : 'Optional'}
      </span>
      {uploadedFile && !uploading && <StatusPill status={uploadedFile.status || 'uploaded'} />}
    </div>
    <UploadZone
      docConfig={docConfig}
      uploadedFile={uploadedFile}
      onUpload={onUpload}
      onDelete={onDelete}
      uploading={uploading}
    />
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// Section
// ─────────────────────────────────────────────────────────────────────────────
const Section = ({ title, icon, accent = 'rust', children }) => (
  <div className="pd-section">
    <div className="pd-section-header">
      <div className={`pd-section-icon pd-section-icon-${accent}`}>{icon}</div>
      <h3 className="pd-section-title">{title}</h3>
    </div>
    {children}
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// ProgressBar
// ─────────────────────────────────────────────────────────────────────────────
const ProgressBar = ({ done, total, label }) => {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)
  return (
    <div className="pd-progress-wrap">
      <div className="pd-progress-row">
        <span className="pd-progress-label">{label}</span>
        <span className="pd-progress-count" style={{ color: pct === 100 ? 'var(--green)' : 'var(--charcoal)' }}>
          {done} / {total}&nbsp;·&nbsp;{pct}%
        </span>
      </div>
      <div className="pd-progress-track">
        <div
          className={`pd-progress-fill ${pct === 100 ? 'complete' : 'partial'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// TabBar
// ─────────────────────────────────────────────────────────────────────────────
const TabBar = ({ tabs, active, onChange }) => (
  <div className="pd-tabs" role="tablist">
    {tabs.map((t) => (
      <button
        key={t.id}
        role="tab"
        aria-selected={active === t.id}
        className={`pd-tab-btn${active === t.id ? ' active' : ''}`}
        onClick={() => onChange(t.id)}
      >
        {t.completedAll
          ? <span className="pd-tab-check">✓</span>
          : t.pendingCount > 0 && <span className="pd-tab-count">{t.pendingCount}</span>
        }
        {t.label}
      </button>
    ))}
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────────────────────────────────────
const Toast = ({ toast, onClose }) => {
  if (!toast) return null
  return (
    <div className={`pd-toast ${toast.type}`} role="alert">
      <span className="pd-toast-icon">{toast.type === 'error' ? '⚠️' : '✓'}</span>
      <span style={{ flex: 1 }}>{toast.msg}</span>
      <button className="pd-toast-close" onClick={onClose}>✕</button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PartnerDocuments — Main Component
// ─────────────────────────────────────────────────────────────────────────────
const PartnerDocuments = () => {
  const [activeTab, setActiveTab]     = useState('owner')
  const [docs, setDocs]               = useState({})   // { owner: {key: fileObj}, [restId]: {key: fileObj} }
  const [uploading, setUploading]     = useState({})
  const [otherDocs, setOtherDocs]     = useState({})
  const [submitting, setSubmitting]   = useState({})
  const [submitted, setSubmitted]     = useState({})
  const [properties, setMyProperties] = useState([])

  // ── Loading state per tab: 'idle' | 'loading' | 'loaded' | 'error'
  const [tabLoadState, setTabLoadState] = useState({ owner: 'idle' })

  const [initializing, setInitializing] = useState(true)  // spinner for first load (properties + owner docs)
  const [toast, setToast]               = useState(null)

  // ─── Toast ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 4000)
  }, [])

  // ─── Fetch properties (restaurant list only, no documents) ─────────────────
  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('partnertoken')
      const res = await fetch(`${host}/partner/fetchproperties`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      })
      if (!res.ok) throw new Error('Failed to fetch properties')
      const data = await res.json()
      setMyProperties(data)
      return data
    } catch (err) {
      console.error('fetchProperties error', err)
      return []
    }
  }

  // ─── Fetch documents for a single tab (owner OR one restaurant) ────────────
  const fetchTabDocuments = useCallback(async (tabKey) => {
    // Guard: skip if already loading or loaded
    setTabLoadState((prev) => {
      if (prev[tabKey] === 'loading' || prev[tabKey] === 'loaded') return prev
      return { ...prev, [tabKey]: 'loading' }
    })

    // Check synchronously — the state setter above is async so we read inline
    // We use a ref-free approach: just proceed, the UI shows spinner while 'loading'
    try {
      const isOwner = tabKey === 'owner'
      const data    = await api.fetchDocuments(isOwner ? [] : [tabKey])

      setDocs((prev) => ({
        ...prev,
        owner:    isOwner ? (data.owner || {}) : prev.owner,
        ...(isOwner ? {} : { [tabKey]: data.restaurants?.[tabKey] || {} }),
      }))

      if (!isOwner && data.otherDocs?.[tabKey]) {
        setOtherDocs((prev) => ({ ...prev, [tabKey]: data.otherDocs[tabKey] }))
      }

      // Restore submitted state from backend response
      if (isOwner && data._ownerSubmitted) {
        setSubmitted((prev) => ({ ...prev, owner: true }))
      }
      if (!isOwner && data.restaurants?.[tabKey]?._submittedForReview) {
        setSubmitted((prev) => ({ ...prev, [tabKey]: true }))
      }

      setTabLoadState((prev) => ({ ...prev, [tabKey]: 'loaded' }))
    } catch (err) {
      console.error(`fetchTabDocuments error [${tabKey}]`, err)
      setTabLoadState((prev) => ({ ...prev, [tabKey]: 'error' }))
    }
  }, [])

  // ─── On mount: load properties + owner docs only ───────────────────────────
  useEffect(() => {
    const init = async () => {
      setInitializing(true)
      await fetchProperties()
      await fetchTabDocuments('owner')
      setInitializing(false)
    }
    init()
  }, [fetchTabDocuments])

  // ─── When activeTab changes, lazy-load that tab's docs if not yet fetched ──
  useEffect(() => {
    if (activeTab === 'owner') return  // already loaded on mount
    setTabLoadState((prev) => {
      // Only trigger fetch if this tab has never been loaded
      if (!prev[activeTab] || prev[activeTab] === 'idle') {
        fetchTabDocuments(activeTab)
      }
      return prev
    })
  }, [activeTab, fetchTabDocuments])

  // ─── Reload current tab (pull-to-refresh style) ────────────────────────────
  const reloadCurrentTab = useCallback(() => {
    setTabLoadState((prev) => ({ ...prev, [activeTab]: 'idle' }))
    fetchTabDocuments(activeTab)
  }, [activeTab, fetchTabDocuments])

  // ─── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async (tabKey, docKey, file) => {
    if (file.size > 10 * 1024 * 1024) {
      showToast('File size exceeds 10 MB limit.', 'error'); return
    }
    if (!/\.(jpe?g|png|webp|pdf|heic)$/i.test(file.name)) {
      showToast('Invalid file type. Use JPG, PNG, WEBP, or PDF.', 'error'); return
    }
    const uk = `${tabKey}__${docKey}`
    setUploading((u) => ({ ...u, [uk]: true }))
    try {
      const result = await api.uploadDocument({
        category:     tabKey === 'owner' ? 'owner' : 'restaurant',
        docType:      docKey,
        restaurantId: tabKey === 'owner' ? undefined : tabKey,
        file,
      })
      setDocs((d) => ({
        ...d,
        [tabKey]: {
          ...d[tabKey],
          [docKey]: {
            docKey:     docKey,
            name:       file.name,
            size:       file.size,
            url:        result.url,
            id:         result.id,
            key:        result.key,
            status:     result.status || 'uploaded',
            uploadedAt: result.uploadedAt || new Date().toISOString(),
          },
        },
      }))
      showToast('Document uploaded successfully.')
    } catch (err) {
      showToast(err.message || 'Upload failed. Please try again.', 'error')
    } finally {
      setUploading((u) => ({ ...u, [uk]: false }))
    }
  }

  // ─── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (tabKey, docKey) => {
    const file = docs[tabKey]?.[docKey]
    if (!file) return
    try {
      await api.deleteDocument(file.id, docKey)
      setDocs((d) => {
        const next = { ...d, [tabKey]: { ...d[tabKey] } }
        delete next[tabKey][docKey]
        return next
      })
      showToast('Document removed.')
    } catch (err) {
      showToast(err.message || 'Delete failed. Please try again.', 'error')
    }
  }

  // ─── Other docs ──────────────────────────────────────────────────────────────
  const handleOtherUpload = async (rid, idx, file) => {
    if (file.size > 10 * 1024 * 1024) { showToast('File exceeds 10 MB.', 'error'); return }
    const uk = `other__${rid}__${idx}`
    setUploading((u) => ({ ...u, [uk]: true }))
    try {
      const slot   = otherDocs[rid][idx]
      const result = await api.uploadDocument({ category: 'other', docType: slot.key, restaurantId: rid, file })
      setOtherDocs((o) => {
        const arr  = [...(o[rid] || [])]
        arr[idx]   = { ...arr[idx], file: { name: file.name, size: file.size, url: result.url, id: result.id, status: result.status || 'uploaded', uploadedAt: result.uploadedAt || new Date().toISOString() } }
        return { ...o, [rid]: arr }
      })
      showToast('Document uploaded successfully.')
    } catch (err) {
      showToast(err.message || 'Upload failed.', 'error')
    } finally {
      setUploading((u) => ({ ...u, [uk]: false }))
    }
  }

  const handleOtherDelete = async (rid, idx) => {
    const slot = otherDocs[rid]?.[idx]
    if (slot?.file?.id) { try { await api.deleteDocument(slot.file.id, slot.key) } catch { /* best effort */ } }
    setOtherDocs((o) => { const arr = [...(o[rid] || [])]; arr.splice(idx, 1); return { ...o, [rid]: arr } })
    showToast('Document removed.')
  }

  const addOtherSlot = (rid) =>
    setOtherDocs((o) => ({ ...o, [rid]: [...(o[rid] || []), { key: `other_${Date.now()}`, label: '', file: null }] }))

  const updateOtherLabel = (rid, idx, label) =>
    setOtherDocs((o) => { const arr = [...(o[rid] || [])]; arr[idx] = { ...arr[idx], label }; return { ...o, [rid]: arr } })

  // ─── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (tabKey) => {
    setSubmitting((s) => ({ ...s, [tabKey]: true }))
    try {
      await api.submitForVerification({
        category:     tabKey === 'owner' ? 'owner' : 'restaurant',
        restaurantId: tabKey === 'owner' ? undefined : tabKey,
      })
      setSubmitted((s) => ({ ...s, [tabKey]: true }))
      showToast("Submitted for verification! We'll review within 24–48 hours.")
    } catch (err) {
      showToast(err.message || 'Submission failed. Please try again.', 'error')
    } finally {
      setSubmitting((s) => ({ ...s, [tabKey]: false }))
    }
  }

  // ─── Completion (only counts loaded tabs) ────────────────────────────────────
  const getCompletion = useCallback((tabKey, docList) => {
    const has = (key) => !!docs[tabKey]?.[key]
    return {
      done:     docList.filter((d) => has(d.key)).length,
      total:    docList.length,
      reqDone:  docList.filter((d) => d.required && has(d.key)).length,
      reqTotal: docList.filter((d) => d.required).length,
    }
  }, [docs])

  const ownerComp      = getCompletion('owner', OWNER_DOCS)
  const activeProperty = properties.find((p) => p._id === activeTab)
  const restaurantComp = activeTab !== 'owner' ? getCompletion(activeTab, RESTAURANT_DOCS) : null

  const tabs = [
    {
      id:           'owner',
      label:        '👤 Owner',
      completedAll: ownerComp.reqDone === ownerComp.reqTotal && ownerComp.reqTotal > 0,
      pendingCount: ownerComp.reqTotal - ownerComp.reqDone,
    },
    ...properties.map((p) => {
      // Only show completion counts for tabs that have been loaded
      const loaded = tabLoadState[p._id] === 'loaded'
      const c      = loaded ? getCompletion(p._id, RESTAURANT_DOCS) : { reqDone: 0, reqTotal: RESTAURANT_DOCS.filter(d => d.required).length }
      return {
        id:           p._id,
        label:        `🍽 ${p.name}`,
        completedAll: loaded && c.reqDone === c.reqTotal && c.reqTotal > 0,
        pendingCount: loaded ? c.reqTotal - c.reqDone : 0,
      }
    }),
  ]

  // ─── Current tab loading state ────────────────────────────────────────────────
  const currentTabState = tabLoadState[activeTab] || 'idle'
  const isTabLoading    = currentTabState === 'loading' || currentTabState === 'idle'

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'var(--font-sans)', background: 'var(--cream)', minHeight: '100vh', padding: '32px 0' }}>

      <Toast toast={toast} onClose={() => setToast(null)} />

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 20px' }}>

        {/* ── Header ── */}
        <div className="pd-header">
          <div className="pd-header-top">
            <div className="pd-header-icon">📋</div>
            <div>
              <h1 className="pd-header-title">Document Verification</h1>
              <p className="pd-header-sub">
                Upload KYC documents for owner verification and compliance documents for each restaurant.
              </p>
            </div>
          </div>

          <div className="pd-overview-pills">
            <div
              className="pd-overview-pill"
              onClick={() => setActiveTab('owner')}
              style={{ borderColor: activeTab === 'owner' ? 'var(--rust)' : undefined }}
            >
              <span className="pd-overview-pill-name">Owner docs</span>
              <span className={`pd-overview-pill-count ${ownerComp.reqDone === ownerComp.reqTotal ? 'complete' : 'partial'}`}>
                {ownerComp.reqDone}/{ownerComp.reqTotal} required
              </span>
            </div>
            {properties.map((p) => {
              const loaded = tabLoadState[p._id] === 'loaded'
              const c      = loaded ? getCompletion(p._id, RESTAURANT_DOCS) : null
              return (
                <div
                  key={p._id}
                  className="pd-overview-pill"
                  onClick={() => setActiveTab(p._id)}
                  style={{ borderColor: activeTab === p._id ? 'var(--rust)' : undefined }}
                >
                  <span className="pd-overview-pill-name">{p.name}</span>
                  <span className={`pd-overview-pill-count ${loaded && c.reqDone === c.reqTotal ? 'complete' : 'partial'}`}>
                    {loaded ? `${c.reqDone}/${c.reqTotal} required` : '— / — required'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Tab Navigation ── */}
        <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

        {/* ── Main Card ── */}
        <div className="pd-card">

          {/* Global initializing spinner (first load only) */}
          {initializing ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <div className="pd-spinner" style={{ justifyContent: 'center' }}>
                <div className="pd-spinner-ring" />
                Loading documents…
              </div>
            </div>

          ) : (
            <>
              {/* ══════════════ OWNER TAB ══════════════ */}
              {activeTab === 'owner' && (
                <>
                  {isTabLoading ? (
                    <TabLoadingSpinner />
                  ) : currentTabState === 'error' ? (
                    <TabError onRetry={reloadCurrentTab} />
                  ) : (
                    <>
                      <ProgressBar done={ownerComp.reqDone} total={ownerComp.reqTotal} label="Required owner documents" />

                      <Section title="Identity & KYC" icon="🪪" accent="rust">
                        {OWNER_DOCS.filter((d) => d.section === 'kyc').map((doc) => (
                          <DocRow
                            key={doc.key}
                            docConfig={doc}
                            uploadedFile={docs['owner']?.[doc.key]}
                            onUpload={(f) => handleUpload('owner', doc.key, f)}
                            onDelete={() => handleDelete('owner', doc.key)}
                            uploading={!!uploading[`owner__${doc.key}`]}
                          />
                        ))}
                      </Section>

                      <Section title="Profile & Banking" icon="🏦" accent="gold">
                        {OWNER_DOCS.filter((d) => d.section === 'profile').map((doc) => (
                          <DocRow
                            key={doc.key}
                            docConfig={doc}
                            uploadedFile={docs['owner']?.[doc.key]}
                            onUpload={(f) => handleUpload('owner', doc.key, f)}
                            onDelete={() => handleDelete('owner', doc.key)}
                            uploading={!!uploading[`owner__${doc.key}`]}
                          />
                        ))}
                      </Section>

                      <SubmitFooter
                        comp={ownerComp}
                        submitted={submitted['owner']}
                        submitting={submitting['owner']}
                        onSubmit={() => handleSubmit('owner')}
                        onReload={reloadCurrentTab}
                      />
                    </>
                  )}
                </>
              )}

              {/* ══════════════ RESTAURANT TAB ══════════════ */}
              {activeTab !== 'owner' && activeProperty && (() => {
                const rid    = activeProperty._id
                const others = otherDocs[rid] || []

                return (
                  <>
                    <div className="pd-rest-header">
                      <div>
                        <div className="pd-rest-name">{activeProperty.name}</div>
                        <div className="pd-rest-id">ID: {rid}</div>
                      </div>
                      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--muted)', textAlign: 'right', lineHeight: 1.7 }}>
                        <div>{activeProperty.completeCount ?? 0} completed orders</div>
                        <div>{activeProperty.onlineCount ?? 0} online now</div>
                      </div>
                    </div>

                    {isTabLoading ? (
                      <TabLoadingSpinner />
                    ) : currentTabState === 'error' ? (
                      <TabError onRetry={reloadCurrentTab} />
                    ) : (
                      <>
                        <ProgressBar done={restaurantComp.reqDone} total={restaurantComp.reqTotal} label="Required restaurant documents" />

                        <Section title="Food & Safety Licenses" icon="🛡️" accent="rust">
                          {RESTAURANT_DOCS.filter((d) => d.section === 'safety').map((doc) => (
                            <DocRow
                              key={doc.key}
                              docConfig={doc}
                              uploadedFile={docs[rid]?.[doc.key]}
                              onUpload={(f) => handleUpload(rid, doc.key, f)}
                              onDelete={() => handleDelete(rid, doc.key)}
                              uploading={!!uploading[`${rid}__${doc.key}`]}
                            />
                          ))}
                        </Section>

                        <Section title="Additional Permits" icon="📜" accent="gold">
                          {RESTAURANT_DOCS.filter((d) => d.section === 'permits').map((doc) => (
                            <DocRow
                              key={doc.key}
                              docConfig={doc}
                              uploadedFile={docs[rid]?.[doc.key]}
                              onUpload={(f) => handleUpload(rid, doc.key, f)}
                              onDelete={() => handleDelete(rid, doc.key)}
                              uploading={!!uploading[`${rid}__${doc.key}`]}
                            />
                          ))}
                        </Section>

                        <Section title="Other Documents" icon="📎" accent="sand">
                          {others.length === 0 && (
                            <p className="pd-empty-other">
                              No additional documents added. Use the button below to attach any other relevant documents — e.g. Liquor License, NOC, FSSAI renewal, etc.
                            </p>
                          )}
                          {others.map((slot, idx) => (
                            <div key={slot.key} className="pd-other-slot">
                              <div className="pd-other-slot-row">
                                <input
                                  className="pd-other-input"
                                  value={slot.label}
                                  onChange={(e) => updateOtherLabel(rid, idx, e.target.value)}
                                  placeholder="Document name — e.g. Liquor License, NOC"
                                />
                                <button className="pd-other-remove-btn" onClick={() => handleOtherDelete(rid, idx)} title="Remove">
                                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <path d="M1 1L12 12M12 1L1 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                              {slot.label.trim() && (
                                <UploadZone
                                  docConfig={{ key: slot.key, label: slot.label, accept: 'image/*,.pdf', hint: `Upload: ${slot.label}`, required: false }}
                                  uploadedFile={slot.file}
                                  onUpload={(f) => handleOtherUpload(rid, idx, f)}
                                  onDelete={() => handleOtherDelete(rid, idx)}
                                  uploading={!!uploading[`other__${rid}__${idx}`]}
                                />
                              )}
                            </div>
                          ))}
                          <button className="pd-add-other-btn" onClick={() => addOtherSlot(rid)}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Add Another Document
                          </button>
                        </Section>

                        <SubmitFooter
                          comp={restaurantComp}
                          submitted={submitted[rid]}
                          submitting={submitting[rid]}
                          onSubmit={() => handleSubmit(rid)}
                          onReload={reloadCurrentTab}
                        />
                      </>
                    )}
                  </>
                )
              })()}
            </>
          )}
        </div>

        {/* ── Footer Note ── */}
        <div className="pd-footer-note">
          <span style={{ fontSize: 15, flexShrink: 0 }}>ℹ️</span>
          <span>
            Documents are reviewed within <strong>24–48 hours</strong>. All files are encrypted in transit and stored securely on <strong>AWS S3</strong>.
            Accepted formats: <strong>JPG, PNG, PDF</strong> — max <strong>10 MB</strong> per file.
            Once verified you will be notified by email and SMS.
          </span>
        </div>

      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Small shared UI pieces
// ─────────────────────────────────────────────────────────────────────────────
const TabLoadingSpinner = () => (
  <div style={{ padding: '48px 0', textAlign: 'center' }}>
    <div className="pd-spinner" style={{ justifyContent: 'center' }}>
      <div className="pd-spinner-ring" />
      Loading documents…
    </div>
  </div>
)

const TabError = ({ onRetry }) => (
  <div style={{ padding: '40px 0', textAlign: 'center' }}>
    <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
    <div style={{ color: 'var(--muted)', marginBottom: 16, fontFamily: 'var(--font-sans)', fontSize: 14 }}>
      Failed to load documents for this tab.
    </div>
    <button
      onClick={onRetry}
      style={{
        padding: '8px 20px', borderRadius: 8, border: '1px solid var(--rust)',
        background: 'transparent', color: 'var(--rust)', cursor: 'pointer',
        fontFamily: 'var(--font-sans)', fontSize: 13,
      }}
    >
      Retry ↺
    </button>
  </div>
)

const SubmitFooter = ({ comp, submitted, submitting, onSubmit, onReload }) => (
  <div className="pd-submit-footer">
    <span className={`pd-submit-note${comp.reqDone === comp.reqTotal ? ' ready' : ''}`}>
      {submitted
        ? '✅ Submitted — under review'
        : comp.reqDone === comp.reqTotal
        ? '✓ All required documents uploaded'
        : `${comp.reqTotal - comp.reqDone} required document(s) remaining`}
    </span>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <button
        onClick={onReload}
        title="Refresh documents"
        style={{
          padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
          background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 14,
        }}
      >
        ↺
      </button>
      <button
        className={`pd-submit-btn ${
          submitting ? 'submitting' :
          comp.reqDone < comp.reqTotal || submitted ? 'disabled' : 'enabled'
        }`}
        onClick={onSubmit}
        disabled={submitting || comp.reqDone < comp.reqTotal || submitted}
      >
        {submitting
          ? <><div className="pd-spinner-ring" style={{ borderTopColor: '#fff', width: 14, height: 14 }} />Submitting…</>
          : submitted ? '✅ Submitted'
          : 'Submit for Verification →'}
      </button>
    </div>
  </div>
)

export default PartnerDocuments